import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable, catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthStateService } from '../auth/auth-state.service';
import { OtpRefreshService } from '../auth/otp-refresh.service';
import { TokenStorageService } from '../auth/token-storage.service';

/**
 * A 401 during an already-active OTP session (the access token expired mid-use, e.g. while the tab
 * slept through the proactive renewal) is first answered with one renewal via the httpOnly refresh
 * cookie and a single retry of the request. Only when the renewal itself is rejected does the
 * session end; a temporary renewal failure (429, 5xx, network) keeps it and passes the original
 * error on. Gated to the OTP session and away from the public auth endpoints, so a wrong/expired OTP
 * code during login doesn't itself trigger a teardown, a 401 from refresh/logout cannot loop, and an
 * MSAL-only session keeps using MSAL's own 401 handling.
 *
 * Assumes every own-API call is issued through `${environment.apiBaseUrl}/api/...` (true across the
 * codebase today via the shared HTTP client setup) — a future relative-URL call to the same backend
 * would bypass this gate.
 *
 * The backend's `TenantAccessFilter` runs ahead of every `/api/v1/**` controller and rejects with
 * 403 the moment the caller's tenant/actor can no longer be resolved (user row deleted, customer
 * disabled, OTP no longer permitted, missing tenant claim) — a session-ending condition, since no
 * view is possible without a resolvable actor. That filter can reject *any* endpoint, whichever the
 * frontend happens to call first, so this cannot be told apart from an ordinary per-resource 403
 * (e.g. a project the caller isn't a member of) by looking at the request URL — the backend marks
 * it with a stable `code` in the JSON error body instead.
 *
 * A 404 from exactly `GET /users/me` means no account exists for the caller's identity any more, so
 * the session is just as pointless and ends the same way. Every other 404 is an ordinary missing
 * resource and keeps the session.
 */
const TENANT_NOT_AUTHORIZED_CODE = 'TENANT_NOT_AUTHORIZED';

function isTenantAccessDenied(error: HttpErrorResponse): boolean {
  return error.error?.code === TENANT_NOT_AUTHORIZED_CODE;
}

function isOwnProfileNotFound(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
  return (
    error.status === 404 && request.method === 'GET' && request.url === `${environment.apiBaseUrl}/api/v1/users/me`
  );
}

function isOwnProtectedApi(url: string): boolean {
  return url.startsWith(`${environment.apiBaseUrl}/api/`) && !url.startsWith(`${environment.apiBaseUrl}/api/v1/auth/`);
}

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const authState = inject(AuthStateService);
  const tokenStorage = inject(TokenStorageService);
  const otpRefresh = inject(OtpRefreshService);
  const msalService = inject(MsalService);
  const router = inject(Router);

  // Cache-only teardown — same shape as authGuard's existing 401/403 branch. It never calls MSAL
  // `logoutRedirect()`; a real SSO sign-out is the `/logout` route's (`logoutGuard`) job. For an
  // Entra session this leaves the IdP session itself intact, so a subsequent login click can
  // silently SSO back in — accepted here because it matches the already-established precedent in
  // authGuard rather than introducing new behavior.
  const clearSessionAndRedirect = () => {
    msalService.instance.clearCache();
    tokenStorage.clear();
    router.navigate(['/login']);
  };

  const endOtpSession = () => {
    authState.setUnauthorized();
    clearSessionAndRedirect();
  };

  const handle = (request: HttpRequest<unknown>, retried: boolean): Observable<HttpEvent<unknown>> => {
    // Read before sending: the inner otpAuthInterceptor attaches exactly this token, and the
    // refresh must know which token the 401 was about to tell a stale one from another tab's renewal.
    const staleToken = tokenStorage.accessToken();
    return next(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 403) {
          authState.setForbidden();
          if (isTenantAccessDenied(error)) {
            clearSessionAndRedirect();
          }
        } else if (isOwnProfileNotFound(request, error)) {
          authState.setForbidden();
          clearSessionAndRedirect();
        } else if (error.status === 401 && tokenStorage.hasSession() && isOwnProtectedApi(request.url)) {
          if (retried) {
            endOtpSession();
            return throwError(() => error);
          }
          return from(otpRefresh.refresh(staleToken)).pipe(
            switchMap((outcome) => {
              if (outcome === 'refreshed') {
                return handle(request, true);
              }
              if (outcome === 'rejected') {
                endOtpSession();
              }
              return throwError(() => error);
            }),
          );
        }
        return throwError(() => error);
      }),
    );
  };

  return handle(req, false);
};
